import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/useAuthStore'
import { isDesktopShell } from '@/lib/desktopShell'
import { defaultLocale, getLocaleFromPath } from '@/lib/locales'

/**
 * Landing Universal Link pour le handoff OAuth Mac.
 *
 * Flow :
 *   1. User clique « Continuer avec Google » dans Callizy.app (WKWebView)
 *   2. La WebView ouvre Safari sur app.callizy.ca/login?desktop_oauth=google
 *   3. Safari finalise le login Google (cookies présents → 1 clic)
 *   4. À la fin du callback, Safari redirige vers app.callizy.ca/auth/desktop-handoff?token=…
 *   5. macOS détecte l'Universal Link → ouvre Callizy.app
 *   6. Cette page (rendue cette fois DANS la WebView Mac) stocke les tokens
 *      et navigue vers le dashboard.
 *
 * Cas fallback :
 *   - L'user voit cette page dans Safari (UL pas reconnue, app pas installée
 *     correctement, etc.) : on affiche un message + on tente le scheme
 *     callizy:// comme dernier recours.
 */
export function DesktopHandoffPage() {
  // No locale in this URL: i18n starts from the saved choice, else the browser language.
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const setTokens = useAuthStore((s) => s.setTokens)
  const startedRef = useRef(false)
  const [showFallback, setShowFallback] = useState(false)

  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true

    const accessToken = searchParams.get('token')
    const refreshToken = searchParams.get('refresh')

    if (!accessToken || !refreshToken) {
      setShowFallback(true)
      return
    }

    if (isDesktopShell()) {
      // On est bien dans la WebView Callizy.app : stocker + naviguer.
      setTokens(accessToken, refreshToken)
      const locale = getLocaleFromPath(window.location.pathname) || defaultLocale
      window.location.replace(`/${locale}/dashboard`)
      return
    }

    // Fallback : on est encore dans Safari (UL pas captée). Tentative de
    // réveil via scheme custom + message à l'user.
    const fallbackScheme =
      `callizy://auth/handoff` +
      `?token=${encodeURIComponent(accessToken)}` +
      `&refresh=${encodeURIComponent(refreshToken)}`
    window.location.href = fallbackScheme
    setTimeout(() => setShowFallback(true), 1500)
  }, [searchParams, setTokens])

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A192F] text-white">
      <div className="text-center max-w-md px-6">
        <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" />
        <h2 className="text-xl font-semibold">
          {showFallback
            ? t('desktopHandoff.openTitle', 'Ouvrez Callizy')
            : t('desktopHandoff.connecting', 'Connexion en cours…')}
        </h2>
        {showFallback ? (
          <p className="text-white/80 mt-2 text-sm">
            {t('desktopHandoff.openHelp')}
          </p>
        ) : (
          <p className="text-white/80 mt-2">
            {t('desktopHandoff.subtitle', 'Transfert sécurisé vers Callizy.app')}
          </p>
        )}
      </div>
    </div>
  )
}
